import React, { useState } from 'react';
import {
    Modal,
    Box,
    IconButton,
    Typography,
    Fade,
    Backdrop,
    Tooltip,
    Paper
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    Share as ShareIcon,
    Info as InfoIcon
} from '@mui/icons-material';

/**
 * Componente de Lightbox para visualização de imagens em tela cheia
 * 
 * @param {Object} props - Propriedades do componente
 * @param {string} props.src - URL da imagem
 * @param {string} props.alt - Texto alternativo para a imagem
 * @param {string} props.title - Título da imagem
 * @param {string} props.description - Descrição da imagem
 * @param {boolean} props.open - Estado de visibilidade do lightbox
 * @param {function} props.onClose - Função para fechar o lightbox
 */
const ImageLightbox2 = ({
    src,
    alt = "Imagem",
    title = "",
    description = "",
    open,
    onClose
}) => {
    // Estado para controle de zoom
    const [zoomLevel, setZoomLevel] = useState(1);
    // Estado para exibir/ocultar informações
    const [showInfo, setShowInfo] = useState(false);

    // Função para fazer o download da imagem
    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = src;
        // Extrair o nome do arquivo da URL ou usar um nome padrão
        const filename = src.substring(src.lastIndexOf('/') + 1) || 'imagem.jpg';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Função para aumentar o zoom
    const handleZoomIn = () => {
        if (zoomLevel < 3) setZoomLevel(prev => prev + 0.25);
    };

    // Função para diminuir o zoom
    const handleZoomOut = () => {
        if (zoomLevel > 0.5) setZoomLevel(prev => prev - 0.25);
    };

    // Função para alternar exibição de informações
    const toggleInfo = () => {
        setShowInfo(prev => !prev);
    };

    // Função para compartilhar (exemplo básico usando clipboard)
    const handleShare = () => {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(src)
                .then(() => {
                    alert('URL da imagem copiada para a área de transferência');
                })
                .catch(err => {
                    console.error('Erro ao copiar URL:', err);
                });
        }
    };

    // Função para resetar zoom ao fechar o lightbox
    const handleClose = () => {
        setZoomLevel(1);
        setShowInfo(false);
        onClose();
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                    style: {
                        backgroundColor: 'rgba(0, 0, 0, 0.85)'
                    }
                },
            }}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                userSelect: 'none',
            }}
        >
            <Fade in={open}>
                <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    outline: 'none',
                }}>
                    {/* Barra de ferramentas superior */}
                    <Paper
                        elevation={3}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            display: 'flex',
                            zIndex: 9999,
                            padding: 0.5,
                            backgroundColor: 'rgba(33, 33, 33, 0.7)',
                            backdropFilter: 'blur(4px)',
                            borderRadius: 2,
                        }}
                    >
                        <Tooltip title="Aumentar zoom">
                            <IconButton
                                onClick={handleZoomIn}
                                sx={{ color: 'white' }}
                                size="small"
                            >
                                <ZoomInIcon />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Diminuir zoom">
                            <IconButton
                                onClick={handleZoomOut}
                                sx={{ color: 'white' }}
                                size="small"
                            >
                                <ZoomOutIcon />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Baixar imagem">
                            <IconButton
                                onClick={handleDownload}
                                sx={{ color: 'white' }}
                                size="small"
                            >
                                <DownloadIcon />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Copiar link da imagem">
                            <IconButton
                                onClick={handleShare}
                                sx={{ color: 'white' }}
                                size="small"
                            >
                                <ShareIcon />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={showInfo ? "Ocultar informações" : "Mostrar informações"}>
                            <IconButton
                                onClick={toggleInfo}
                                sx={{ color: showInfo ? 'primary.light' : 'white' }}
                                size="small"
                            >
                                <InfoIcon />
                            </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Fechar">
                            <IconButton
                                onClick={handleClose}
                                sx={{ color: 'white' }}
                                size="small"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Paper>

                    {/* Container da imagem com zoom */}
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            cursor: zoomLevel > 1 ? 'move' : 'default',
                        }}
                    >
                        <img
                            src={src}
                            alt={alt}
                            style={{
                                maxWidth: `${100 * zoomLevel}%`,
                                maxHeight: `${100 * zoomLevel}%`,
                                objectFit: 'contain',
                                transition: 'transform 0.2s ease',
                                transform: `scale(${zoomLevel})`,
                            }}
                        />
                    </Box>

                    {/* Painel de informações */}
                    {showInfo && (title || description) && (
                        <Paper
                            elevation={3}
                            sx={{
                                position: 'absolute',
                                bottom: 16,
                                left: 16,
                                right: 16,
                                padding: 2,
                                backgroundColor: 'rgba(33, 33, 33, 0.7)',
                                backdropFilter: 'blur(4px)',
                                color: 'white',
                                borderRadius: 2,
                                maxWidth: '800px',
                                margin: '0 auto',
                            }}
                        >
                            {title && (
                                <Typography variant="h6" component="h2" gutterBottom>
                                    {title}
                                </Typography>
                            )}
                            {description && (
                                <Typography variant="body2">
                                    {description}
                                </Typography>
                            )}
                        </Paper>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default ImageLightbox2;